import { colors } from "../styles/colors";
import { Link } from "react-router-dom";
import { ArrowRight, Home } from "lucide-react";
import SponsorPlaceholder from "../components/SponsorPlaceholder";
import ScrollToTop from "../components/ScrollToTop";

export default function ForceMajeureClause() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      <header className="px-6 md:px-12 lg:px-24 py-6 " style={{ borderColor: colors.border }}>
        <Link to="/" data-testid="home-link" className="inline-flex items-center gap-2 text-lg font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          <img src="/clauseclear-logo.png" alt="ClauseClear" className="h-10 object-contain" />

        </Link>
      </header>

      <div className="h-32 md:h-48" style={{ background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.background} 50%, ${colors.backgroundLight} 100%)` }} />

      <article className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-4xl mx-auto">
          <h1 data-testid="page-title" className="text-4xl md:text-5xl font-light tracking-tight leading-[1.1] mb-8" style={{ color: colors.text }}>
            What Does a Force Majeure Clause Mean?
          </h1>

          <div className="prose prose-lg max-w-none">
            <section className="mb-12">
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: colors.textMuted }}>
                A force majeure clause excuses non-performance of contractual obligations when extraordinary events beyond anyone's control make performance impossible or impractical.
              </p>
            </section>

            <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Example Clause</h2>
              <div className="p-6 rounded-xl text-base" style={{ backgroundColor: colors.backgroundDark, color: colors.text, fontFamily: 'monospace' }}>
                "Neither party shall be liable for failure or delay in performance due to force majeure events including but not limited to acts of God, war, terrorism, pandemic, natural disasters, government actions, labor disputes, or any other cause beyond the reasonable control of the affected party, provided that prompt notice is given and reasonable efforts are made to resume performance."
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Plain-English Explanation</h2>
              <p className="text-base md:text-lg leading-relaxed mb-4" style={{ color: colors.textMuted }}>
                This clause provides an escape route when unforeseeable disasters make it impossible to fulfill the contract. Common examples include:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Natural disasters (earthquakes, floods, hurricanes)
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Wars, civil unrest, or acts of terrorism
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Pandemics or public health emergencies
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Government orders or legal changes that prevent performance
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Notable Wording</h2>
              <ul className="space-y-3 ml-6">
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>"Beyond reasonable control"</strong> - must be truly unforeseeable and unavoidable
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>"Prompt notice"</strong> - you must inform the other party quickly
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  <strong>"Reasonable efforts"</strong> - you can't just give up; must try to work around it
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Common Purpose</h2>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Force majeure clauses allocate risk for catastrophic events that no one could reasonably plan for or prevent. They protect both parties from liability when external circumstances make contract performance impossible. Without this clause, failing to perform could be considered a breach even when caused by events entirely outside your control.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Typical Effects</h2>
              <ul className="space-y-3 ml-6">
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Temporarily suspends performance obligations
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Prevents liability for delays or non-performance during the event
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  May allow contract termination if disruption continues too long
                </li>
                <li className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                  Doesn't usually excuse payment obligations already due
                </li>
              </ul>
            </section>

            <section className="mb-12 rounded-2xl p-8" style={{ backgroundColor: colors.backgroundLight, border: `1px solid ${colors.border}` }} style={{ borderColor: colors.border }}>
              <h2 className="text-2xl font-medium mb-4" style={{ color: colors.accent }}>Important Context</h2>
              <p className="text-base md:text-lg leading-relaxed" style={{ color: colors.textMuted }}>
                Not every difficulty qualifies as force majeure—financial hardship, market changes, or routine business challenges typically don't count. The specific list of qualifying events matters significantly. Some contracts require the affected party to prove they took mitigation steps. Legal interpretation varies by jurisdiction and depends heavily on the exact wording. This explanation covers typical contract language but doesn't constitute legal advice about your specific situation.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-2xl font-medium mb-6" style={{ color: colors.accent }}>Related Clauses</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Link to="/explain-cancellation-clause" data-testid="related-link-cancellation" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                  <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Cancellation Clause</h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>Learn about ending agreements</p>
                </Link>
                <Link to="/explain-variation-clause" data-testid="related-link-variation" className="p-6 rounded-xl hover:shadow-md transition-all" style={{ borderColor: colors.border }}>
                  <h3 className="font-medium mb-2" style={{ color: colors.accent }}>Variation Clause</h3>
                  <p className="text-sm" style={{ color: colors.textMuted }}>Understand contract modifications</p>
                </Link>
              </div>
            </section>

            <div data-testid="cta-section" className="text-center p-8 rounded-2xl" style={{ borderColor: colors.border }}>
              <p className="text-lg mb-4" style={{ color: colors.textMuted }}>Want to understand your own clause?</p>
              <Link to="/" data-testid="cta-button" className="inline-flex items-center gap-2 text-white hover:opacity-90 rounded-full px-8 py-4 text-lg font-medium transition-all" style={{ backgroundColor: '#2F3E30' }}>
                Try ClauseClear
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>

            {/* Sponsorship Placeholder */}
            <SponsorPlaceholder variant="full" />
          </div>
        </div>
      </article>

      <footer className="px-6 md:px-12 lg:px-24 py-12 text-center " style={{ borderColor: colors.border }}>
        <p className="text-sm mb-2" style={{ color: colors.textMuted }}>ClauseClear is an educational tool. It does not provide legal advice.</p>
        <a href="https://buymeacoffee.com/jamesmoneyz" target="_blank" rel="noopener noreferrer" className="text-base font-medium hover:opacity-80 transition-opacity" style={{ color: colors.accent }}>
          Support the project
        </a>
      </footer>
    </div>
  );
}
